import React, { useEffect, useRef, useState, useMemo } from 'react';
import { AlertCircle, Bell, Check, CheckCheck, X } from 'lucide-react';
import { calculateOptimalPopupPosition, cn } from '../../lib/utils';
import { useOutsideClick } from '../../hooks/useOutsideClick';
import { useNavigate } from 'react-router-dom';
import { useViewport } from '../../contexts/ViewportContext';
import { useStatusBarInfo } from '../../hooks/useStatusBarInfo';
import { logger } from '../../utils/logger';
import { useUser } from '../../hooks/queries/useAuth';
import {
  useNotifications,
  useMarkNotificationAsRead,
  useMarkAllNotificationsAsRead,
} from '../../hooks/queries/useNotifications';
import { useNotificationSubscription } from '../../hooks/useNotificationSubscription';
import { Notification, formatNotification } from '../../hooks/queries/notificationTypes';

const NotificationCenter: React.FC = () => {
  // Get user for notification subscription
  const { data: user } = useUser();

  // Use React Query hooks for notifications
  const { data, isLoading, error, refetch } = useNotifications();
  const markAsReadMutation = useMarkNotificationAsRead();
  const markAllAsReadMutation = useMarkAllNotificationsAsRead();

  // Extract notifications and unread count from the data
  const notifications = data?.notifications || [];
  const unreadCount = data?.unreadCount || 0;

  const [isOpen, setIsOpen] = useState(false);
  const notificationRef = useRef<HTMLDivElement>(null);
  const navigate = useNavigate();
  const { isMobile } = useViewport();
  const { isNativeApp, statusBarHeight } = useStatusBarInfo();

  // Format notifications for display
  const formattedNotifications = useMemo(() => {
    return notifications.map(notification => formatNotification(notification));
  }, [notifications]);

  // Note: Notification refreshes are handled automatically by React Query
  // This ensures coordinated refreshes and prevents multiple redundant API calls

  // Log state changes for debugging
  useEffect(() => {
    logger.component('Notification panel state changed', { isOpen });
  }, [isOpen]);

  // Close when clicking outside
  useOutsideClick(
    notificationRef,
    () => {
      // Don't handle outside clicks on mobile - the panel has its own close button
      if (isMobile) {
        return;
      }

      if (isOpen) {
        setIsOpen(false);
      }
    },
    isOpen
  );

  // Set up notification subscription for the current user
  useNotificationSubscription(user?.id || null);

  // Retry loading data if there was an error
  useEffect(() => {
    if (error) {
      // Try to refetch after a delay
      const retryTimer = setTimeout(() => {
        refetch();
      }, 5000);

      return () => clearTimeout(retryTimer);
    }
  }, [error, refetch]);

  // Handle clicking a notification - navigate to the resource
  const handleNotificationClick = (notification: Notification) => {
    // Mark as read using the mutation
    markAsReadMutation.mutate({ notificationId: notification.id });

    // Navigate to the appropriate location based on resource type
    if (notification.topic_id) {
      // Navigate to the topic
      navigate(`/project/${notification.project_id}/topic/${notification.topic_id}`);

      // Close the notification panel
      setIsOpen(false);
    }
  };

  // We now use the formatNotification utility from notificationTypes.ts instead of this local function

  // Function to handle scrolling
  useEffect(() => {
    if (isOpen && isMobile) {
      // Add body class to prevent scrolling when notification center is open on mobile
      document.body.classList.add('overflow-hidden');
    } else {
      document.body.classList.remove('overflow-hidden');
    }

    return () => {
      document.body.classList.remove('overflow-hidden');
    };
  }, [isOpen, isMobile]);

  // Display loading state
  const renderLoadingState = () => (
    <div className="flex justify-center items-center p-8">
      <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-gray-400"></div>
    </div>
  );

  // Display error state
  const renderErrorState = () => (
    <div className="flex flex-col items-center justify-center p-8 text-red-500">
      <AlertCircle size={24} className="mb-2" />
      <p>Failed to load notifications</p>
      <button onClick={() => refetch()} className="mt-2 text-sm text-blue-500 hover:text-blue-700">
        Retry
      </button>
    </div>
  );

  // Notification content renderer
  const renderNotificationContent = () => (
    <>
      {/* Header - Styled to match mobile drawer header */}
      <div
        className={cn(
          'p-4 flex justify-between items-center bg-black',
          isMobile
            ? 'border-b border-gray-800' // Border for mobile
            : '' // No border for desktop
        )}
        style={{
          paddingTop: isMobile && isNativeApp ? `${statusBarHeight + 16}px` : '1rem',
        }}
      >
        <h3 className="text-lg font-medium text-white">Notifications</h3>
        <div className="flex items-center gap-3">
          {unreadCount > 0 && (
            <button
              onClick={() => markAllAsReadMutation.mutate()}
              className="text-sm text-gray-400 hover:text-white flex items-center gap-1"
              disabled={markAllAsReadMutation.isPending}
            >
              <CheckCheck size={16} />
              <span className="hidden sm:inline">Mark all read</span>
            </button>
          )}
          <button
            onClick={() => {
              logger.component('Notification close button clicked');
              setIsOpen(false);
            }}
            className="text-gray-400 hover:text-white transition-colors"
          >
            <X size={isMobile ? 20 : 18} />
          </button>
        </div>
      </div>

      {/* Show loading state */}
      {isLoading && renderLoadingState()}

      {/* Show error state */}
      {error && renderErrorState()}

      {/* Notification list - only show when not loading and no error */}
      {!isLoading && !error && (
        <div className="divide-y divide-gray-200 bg-white max-h-[50vh] overflow-y-auto">
          {formattedNotifications.length === 0 ? (
            <div className="p-6 text-center text-gray-500">No notifications yet</div>
          ) : (
            formattedNotifications.map(notification => (
              <div
                key={notification.id}
                className={cn(
                  'p-4 cursor-pointer transition-colors',
                  !notification.is_read
                    ? 'bg-blue-50 hover:bg-blue-100'
                    : 'bg-white hover:bg-gray-50'
                )}
                onClick={() => handleNotificationClick(notification)}
              >
                <div className="flex justify-between items-start">
                  <div className="flex-1">
                    <p
                      className={cn(
                        'text-sm text-gray-900',
                        !notification.is_read && 'font-medium'
                      )}
                    >
                      {notification.formattedMessage}
                    </p>
                    <p className="text-xs text-gray-500 mt-1">{notification.formattedTime}</p>
                  </div>

                  {!notification.is_read && (
                    <button
                      onClick={e => {
                        e.stopPropagation();
                        markAsReadMutation.mutate({ notificationId: notification.id });
                      }}
                      className="text-blue-500 hover:text-blue-700"
                      aria-label="Mark as read"
                      disabled={markAsReadMutation.isPending}
                    >
                      <Check size={16} />
                    </button>
                  )}
                </div>
              </div>
            ))
          )}
        </div>
      )}
    </>
  );

  return (
    <div ref={notificationRef} className="relative z-20">
      {/* Notification button with badge */}
      <button
        className="relative p-2 text-gray-500 hover:text-gray-700 rounded-lg transition-colors flex-shrink-0"
        onClick={() => {
          const newState = !isOpen;
          logger.component('Notification toggle clicked', { newState });
          setIsOpen(newState);
        }}
        aria-label="Toggle notifications"
        style={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}
      >
        <Bell size={20} />
        {unreadCount > 0 && (
          <span className="absolute top-0 right-0 block h-5 w-5 rounded-full bg-red-500 text-white text-xs font-medium flex items-center justify-center transform translate-x-1 -translate-y-1 animate-pulse">
            {unreadCount > 9 ? '9+' : unreadCount}
          </span>
        )}
      </button>

      {/* Notification overlay for mobile (same as MobileDrawer) */}
      {isOpen && isMobile && (
        <div
          className="fixed inset-0 bg-black/50 backdrop-blur-sm z-40"
          onClick={() => setIsOpen(false)}
        />
      )}

      {/* Mobile panel - Slide down/up variation */}
      {isMobile ? (
        <div
          className={cn(
            'fixed inset-x-0 top-0 z-50 w-full h-full transform transition-transform duration-300 ease-in-out flex flex-col',
            isOpen ? 'translate-y-0' : '-translate-y-full'
          )}
        >
          {renderNotificationContent()}
        </div>
      ) : (
        /* Desktop panel - only show when open */
        isOpen && (
          <div
            style={(() => {
              const buttonRect = notificationRef.current
                ?.querySelector('button')
                ?.getBoundingClientRect();

              if (buttonRect) {
                // Desktop positioning - to the right of the button with offset
                const position = calculateOptimalPopupPosition(
                  { top: buttonRect.bottom, left: buttonRect.right - 280 }, // Position to right with offset
                  { width: 340, height: 400 },
                  { x: 12, y: 8 } // Added more x-offset to avoid being too close to border
                );

                return {
                  top: `${position.top}px`,
                  left: `${position.left}px`,
                  position: 'fixed',
                };
              }

              return {};
            })()}
            className="fixed w-[340px] rounded-md max-h-[80vh] overflow-hidden shadow-lg z-50"
          >
            {renderNotificationContent()}
          </div>
        )
      )}
    </div>
  );
};

export default NotificationCenter;
